// Pure helpers behind the flag review popup (FlagReviewDialog). Kept in a
// plain module so they can be tested without mounting MUI.

/**
 * entry_snapshot is written as an object, but older rows (and any future
 * import path) may hold a JSON string. Never throw on either.
 */
export const parseSnapshot = (snapshot) => {
  if (!snapshot) return null;
  if (typeof snapshot === "string") {
    try {
      const parsed = JSON.parse(snapshot);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  }
  return typeof snapshot === "object" ? snapshot : null;
};

// The same normalisation aiTriageService uses for duplicate detection.
export const normaliseEvent = (event) =>
  String(event || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

// Whole days between two dates, or null when either is unusable.
export const daysBetween = (a, b) => {
  if (!a || !b) return null;
  const first = new Date(a);
  const second = new Date(b);
  if (Number.isNaN(first.getTime()) || Number.isNaN(second.getTime())) {
    return null;
  }
  return Math.abs(Math.round((first - second) / 86400000));
};

// Fields captured in entry_snapshot (supabase flagService.captureEntrySnapshot)
// and the lookup column that names the current value.
const SNAPSHOT_FIELDS = [
  { key: "event", label: "Event" },
  { key: "event_date", label: "Date", asDate: true },
  { key: "points", label: "Points" },
  { key: "position", label: "Position", nameKey: "position_name" },
  { key: "district", label: "District" },
  { key: "level", label: "Level", nameKey: "level_name" },
  { key: "tier", label: "Tier", nameKey: "tier_name" },
  { key: "category", label: "Category", nameKey: "category_name" },
];

// Lookup joins (category in particular) replace the raw id with an embedded
// object, so compare and display the object's id/name rather than the object.
const scalarValue = (value) =>
  value && typeof value === "object" ? value.id ?? value.name ?? null : value;

const comparableValue = (field, rawValue) => {
  const value = scalarValue(rawValue);
  if (value === null || value === undefined || value === "") return "";
  if (field.asDate) return String(value).slice(0, 10);
  if (field.key === "points") return String(Number(value));
  return String(value).trim();
};

const displayValue = (field, rawValue, entry) => {
  const value = scalarValue(rawValue);
  const raw =
    value === null || value === undefined || value === "" ? "—" : value;
  if (field.asDate && raw !== "—") return String(raw).slice(0, 10);

  const name = entry?.[field.nameKey];
  // entryService falls back to "ID: 12" when the lookup row is missing;
  // appending that to the raw id is noise.
  if (name && !String(name).startsWith("ID:") && String(name) !== String(raw)) {
    return `${raw} (${name})`;
  }
  return String(raw);
};

/**
 * Compare a flag's entry_snapshot with the entry as it is now.
 * Returns [{ label, from, to }] for the watched fields that changed.
 */
export const diffSnapshot = (snapshot, entry) => {
  if (!snapshot || !entry) return [];
  return SNAPSHOT_FIELDS.filter(
    (field) =>
      comparableValue(field, snapshot[field.key]) !==
      comparableValue(field, entry[field.key]),
  ).map((field) => ({
    label: field.label,
    from: displayValue(field, snapshot[field.key], null),
    to: displayValue(field, entry[field.key], entry),
  }));
};
